<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectRisksAPI from 'dashboard/api/projectRisks';
import ProjectIssuesAPI from 'dashboard/api/projectIssues';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();

const RISK_LEVELS = ['low', 'medium', 'high'];
const RESPONSE_STRATEGIES = ['avoid', 'mitigate', 'transfer', 'accept'];
const RISK_STATUSES = ['open', 'mitigating', 'closed', 'realized'];
const ISSUE_SEVERITIES = ['low', 'medium', 'high'];
const ISSUE_STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

const SCORE_WEIGHT = { low: 1, medium: 2, high: 3 };

const RISK_STATUS_CLASS = {
  open: 'bg-n-ruby-3 text-n-ruby-11',
  mitigating: 'bg-n-amber-3 text-n-amber-11',
  closed: 'bg-n-slate-3 text-n-slate-11',
  realized: 'bg-n-blue-3 text-n-blue-11',
};

const ISSUE_STATUS_CLASS = {
  open: 'bg-n-ruby-3 text-n-ruby-11',
  in_progress: 'bg-n-amber-3 text-n-amber-11',
  resolved: 'bg-n-teal-3 text-n-teal-11',
  closed: 'bg-n-slate-3 text-n-slate-11',
};

const emptyRisk = () => ({
  title: '',
  description: '',
  category: '',
  probability: 'medium',
  impact: 'medium',
  response_strategy: 'mitigate',
  response_plan: '',
  status: 'open',
});

const emptyIssue = () => ({
  title: '',
  description: '',
  severity: 'medium',
  status: 'open',
});

const risks = ref([]);
const issues = ref([]);
const isLoading = ref(false);

const riskForm = ref(emptyRisk());
const editingRiskId = ref(null);
const showRiskForm = ref(false);
const isSavingRisk = ref(false);

const issueForm = ref(emptyIssue());
const editingIssueId = ref(null);
const showIssueForm = ref(false);
const isSavingIssue = ref(false);
const promotingIssueId = ref(null);

const riskScore = risk =>
  (SCORE_WEIGHT[risk.probability] || 0) * (SCORE_WEIGHT[risk.impact] || 0);

const riskScoreClass = risk => {
  const score = riskScore(risk);
  if (score >= 6) return 'bg-n-ruby-3 text-n-ruby-11';
  if (score >= 3) return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-teal-3 text-n-teal-11';
};

const ownerName = entry =>
  entry?.owner?.name || entry?.owner_name || t('PROJECTS.RISKS.NO_OWNER');

const loadAll = async () => {
  isLoading.value = true;
  try {
    const [riskRes, issueRes] = await Promise.all([
      ProjectRisksAPI.get(props.projectId),
      ProjectIssuesAPI.get(props.projectId),
    ]);
    risks.value = riskRes.data || [];
    issues.value = issueRes.data || [];
  } catch (error) {
    risks.value = [];
    issues.value = [];
  } finally {
    isLoading.value = false;
  }
};

// --- Risk register ---
const openNewRisk = () => {
  riskForm.value = emptyRisk();
  editingRiskId.value = null;
  showRiskForm.value = true;
};

const openEditRisk = risk => {
  riskForm.value = {
    title: risk.title || '',
    description: risk.description || '',
    category: risk.category || '',
    probability: risk.probability || 'medium',
    impact: risk.impact || 'medium',
    response_strategy: risk.response_strategy || 'mitigate',
    response_plan: risk.response_plan || '',
    status: risk.status || 'open',
  };
  editingRiskId.value = risk.id;
  showRiskForm.value = true;
};

const cancelRiskForm = () => {
  showRiskForm.value = false;
  editingRiskId.value = null;
};

const saveRisk = async () => {
  if (!riskForm.value.title.trim() || isSavingRisk.value) return;
  isSavingRisk.value = true;
  try {
    if (editingRiskId.value) {
      await ProjectRisksAPI.update(
        props.projectId,
        editingRiskId.value,
        riskForm.value
      );
    } else {
      await ProjectRisksAPI.create(props.projectId, riskForm.value);
    }
    showRiskForm.value = false;
    editingRiskId.value = null;
    await loadAll();
  } catch (error) {
    useAlert(t('PROJECTS.RISKS.SAVE_ERROR'));
  } finally {
    isSavingRisk.value = false;
  }
};

const deleteRisk = async risk => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.RISKS.DELETE_CONFIRM'))) return;
  try {
    await ProjectRisksAPI.delete(props.projectId, risk.id);
    risks.value = risks.value.filter(r => r.id !== risk.id);
  } catch (error) {
    useAlert(t('PROJECTS.RISKS.DELETE_ERROR'));
  }
};

// --- Issue log ---
const openNewIssue = () => {
  issueForm.value = emptyIssue();
  editingIssueId.value = null;
  showIssueForm.value = true;
};

const openEditIssue = issue => {
  issueForm.value = {
    title: issue.title || '',
    description: issue.description || '',
    severity: issue.severity || 'medium',
    status: issue.status || 'open',
  };
  editingIssueId.value = issue.id;
  showIssueForm.value = true;
};

const cancelIssueForm = () => {
  showIssueForm.value = false;
  editingIssueId.value = null;
};

const saveIssue = async () => {
  if (!issueForm.value.title.trim() || isSavingIssue.value) return;
  isSavingIssue.value = true;
  try {
    if (editingIssueId.value) {
      await ProjectIssuesAPI.update(
        props.projectId,
        editingIssueId.value,
        issueForm.value
      );
    } else {
      await ProjectIssuesAPI.create(props.projectId, issueForm.value);
    }
    showIssueForm.value = false;
    editingIssueId.value = null;
    await loadAll();
  } catch (error) {
    useAlert(t('PROJECTS.ISSUES.SAVE_ERROR'));
  } finally {
    isSavingIssue.value = false;
  }
};

const deleteIssue = async issue => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.ISSUES.DELETE_CONFIRM'))) return;
  try {
    await ProjectIssuesAPI.delete(props.projectId, issue.id);
    issues.value = issues.value.filter(i => i.id !== issue.id);
  } catch (error) {
    useAlert(t('PROJECTS.ISSUES.DELETE_ERROR'));
  }
};

const linkedTask = issue =>
  issue?.task ||
  issue?.linked_task ||
  (issue?.task_id ? { id: issue.task_id } : null);

const taskLabel = issue => {
  const task = linkedTask(issue);
  if (!task) return null;
  return task.title || `#${task.display_id ?? task.id}`;
};

const promoteToTask = async issue => {
  if (promotingIssueId.value) return;
  promotingIssueId.value = issue.id;
  try {
    await ProjectIssuesAPI.promoteToTask(props.projectId, issue.id);
    useAlert(t('PROJECTS.ISSUES.PROMOTE_SUCCESS'));
    await loadAll();
  } catch (error) {
    useAlert(t('PROJECTS.ISSUES.PROMOTE_ERROR'));
  } finally {
    promotingIssueId.value = null;
  }
};

watch(() => props.projectId, loadAll);
onMounted(loadAll);
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Risk register -->
    <section class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PROJECTS.RISKS.TITLE') }}
        </h3>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('PROJECTS.RISKS.ADD')"
          @click="openNewRisk"
        />
      </div>

      <!-- Risk form -->
      <div
        v-if="showRiskForm"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <input
          v-model="riskForm.title"
          type="text"
          :placeholder="t('PROJECTS.RISKS.FIELDS.TITLE')"
          class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <textarea
          v-model="riskForm.description"
          rows="2"
          :placeholder="t('PROJECTS.RISKS.FIELDS.DESCRIPTION')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            v-model="riskForm.category"
            type="text"
            :placeholder="t('PROJECTS.RISKS.FIELDS.CATEGORY')"
            class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.RISKS.FIELDS.PROBABILITY') }}
            <select
              v-model="riskForm.probability"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="lvl in RISK_LEVELS" :key="lvl" :value="lvl">
                {{ t(`PROJECTS.RISKS.LEVELS.${lvl.toUpperCase()}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.RISKS.FIELDS.IMPACT') }}
            <select
              v-model="riskForm.impact"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="lvl in RISK_LEVELS" :key="lvl" :value="lvl">
                {{ t(`PROJECTS.RISKS.LEVELS.${lvl.toUpperCase()}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.RISKS.FIELDS.RESPONSE_STRATEGY') }}
            <select
              v-model="riskForm.response_strategy"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option
                v-for="strat in RESPONSE_STRATEGIES"
                :key="strat"
                :value="strat"
              >
                {{ t(`PROJECTS.RISKS.STRATEGIES.${strat.toUpperCase()}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.RISKS.FIELDS.STATUS') }}
            <select
              v-model="riskForm.status"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="st in RISK_STATUSES" :key="st" :value="st">
                {{ t(`PROJECTS.RISKS.STATUSES.${st.toUpperCase()}`) }}
              </option>
            </select>
          </label>
        </div>
        <textarea
          v-model="riskForm.response_plan"
          rows="2"
          :placeholder="t('PROJECTS.RISKS.FIELDS.RESPONSE_PLAN')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <div class="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('PROJECTS.RISKS.CANCEL')"
            @click="cancelRiskForm"
          />
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.RISKS.SAVE')"
            :is-loading="isSavingRisk"
            :disabled="!riskForm.title.trim()"
            @click="saveRisk"
          />
        </div>
      </div>

      <!-- Risk table -->
      <ProjectEmptyState
        v-if="!risks.length && !isLoading"
        compact
        icon="i-lucide-triangle-alert"
        :title="t('PROJECTS.RISKS.EMPTY')"
      />
      <div
        v-else-if="risks.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-10 bg-n-solid-2">
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.TITLE') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.PROBABILITY') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.IMPACT') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.SCORE') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.RESPONSE_STRATEGY') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.STATUS') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.RISKS.FIELDS.OWNER') }}
              </th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak/60">
            <tr
              v-for="risk in risks"
              :key="risk.id"
              class="transition-colors group hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2 text-n-slate-12">{{ risk.title }}</td>
              <td class="px-3 py-2 text-n-slate-11">
                {{
                  t(
                    `PROJECTS.RISKS.LEVELS.${(risk.probability || 'low').toUpperCase()}`
                  )
                }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{
                  t(
                    `PROJECTS.RISKS.LEVELS.${(risk.impact || 'low').toUpperCase()}`
                  )
                }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md tabular-nums"
                  :class="riskScoreClass(risk)"
                >
                  {{ riskScore(risk) }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{
                  risk.response_strategy
                    ? t(
                        `PROJECTS.RISKS.STRATEGIES.${risk.response_strategy.toUpperCase()}`
                      )
                    : '—'
                }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md"
                  :class="
                    RISK_STATUS_CLASS[risk.status] || RISK_STATUS_CLASS.open
                  "
                >
                  {{
                    t(
                      `PROJECTS.RISKS.STATUSES.${(risk.status || 'open').toUpperCase()}`
                    )
                  }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ ownerName(risk) }}</td>
              <td class="px-3 py-2">
                <div
                  class="flex items-center justify-end gap-1 transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <Button
                    v-tooltip.top="t('PROJECTS.RISKS.EDIT')"
                    variant="ghost"
                    color="slate"
                    size="xs"
                    icon="i-lucide-pencil"
                    @click="openEditRisk(risk)"
                  />
                  <Button
                    v-tooltip.top="t('PROJECTS.RISKS.DELETE')"
                    variant="ghost"
                    color="ruby"
                    size="xs"
                    icon="i-lucide-trash-2"
                    @click="deleteRisk(risk)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Issue log -->
    <section class="flex flex-col gap-3">
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PROJECTS.ISSUES.TITLE') }}
        </h3>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('PROJECTS.ISSUES.ADD')"
          @click="openNewIssue"
        />
      </div>

      <!-- Issue form -->
      <div
        v-if="showIssueForm"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <input
          v-model="issueForm.title"
          type="text"
          :placeholder="t('PROJECTS.ISSUES.FIELDS.TITLE')"
          class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <textarea
          v-model="issueForm.description"
          rows="2"
          :placeholder="t('PROJECTS.ISSUES.FIELDS.DESCRIPTION')"
          class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.ISSUES.FIELDS.SEVERITY') }}
            <select
              v-model="issueForm.severity"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="sev in ISSUE_SEVERITIES" :key="sev" :value="sev">
                {{ t(`PROJECTS.ISSUES.SEVERITIES.${sev.toUpperCase()}`) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-10">
            {{ t('PROJECTS.ISSUES.FIELDS.STATUS') }}
            <select
              v-model="issueForm.status"
              class="w-full h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
            >
              <option v-for="st in ISSUE_STATUSES" :key="st" :value="st">
                {{ t(`PROJECTS.ISSUES.STATUSES.${st.toUpperCase()}`) }}
              </option>
            </select>
          </label>
        </div>
        <div class="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('PROJECTS.ISSUES.CANCEL')"
            @click="cancelIssueForm"
          />
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.ISSUES.SAVE')"
            :is-loading="isSavingIssue"
            :disabled="!issueForm.title.trim()"
            @click="saveIssue"
          />
        </div>
      </div>

      <!-- Issue table -->
      <ProjectEmptyState
        v-if="!issues.length && !isLoading"
        compact
        icon="i-lucide-bug"
        :title="t('PROJECTS.ISSUES.EMPTY')"
      />
      <div
        v-else-if="issues.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-10 bg-n-solid-2">
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.ISSUES.FIELDS.TITLE') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.ISSUES.FIELDS.SEVERITY') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.ISSUES.FIELDS.STATUS') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.ISSUES.FIELDS.OWNER') }}
              </th>
              <th class="px-3 py-2 font-normal">
                {{ t('PROJECTS.ISSUES.FIELDS.LINKED_TASK') }}
              </th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak/60">
            <tr
              v-for="issue in issues"
              :key="issue.id"
              class="transition-colors group hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2 text-n-slate-12">{{ issue.title }}</td>
              <td class="px-3 py-2 text-n-slate-11">
                {{
                  t(
                    `PROJECTS.ISSUES.SEVERITIES.${(issue.severity || 'low').toUpperCase()}`
                  )
                }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md"
                  :class="
                    ISSUE_STATUS_CLASS[issue.status] || ISSUE_STATUS_CLASS.open
                  "
                >
                  {{
                    t(
                      `PROJECTS.ISSUES.STATUSES.${(issue.status || 'open').toUpperCase()}`
                    )
                  }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ ownerName(issue) }}</td>
              <td class="px-3 py-2">
                <span v-if="taskLabel(issue)" class="text-n-blue-text">
                  {{ taskLabel(issue) }}
                </span>
                <Button
                  v-else
                  variant="faded"
                  color="slate"
                  size="xs"
                  icon="i-lucide-arrow-up-right"
                  :label="t('PROJECTS.ISSUES.PROMOTE')"
                  :is-loading="promotingIssueId === issue.id"
                  @click="promoteToTask(issue)"
                />
              </td>
              <td class="px-3 py-2">
                <div
                  class="flex items-center justify-end gap-1 transition-opacity opacity-0 group-hover:opacity-100"
                >
                  <Button
                    v-tooltip.top="t('PROJECTS.ISSUES.EDIT')"
                    variant="ghost"
                    color="slate"
                    size="xs"
                    icon="i-lucide-pencil"
                    @click="openEditIssue(issue)"
                  />
                  <Button
                    v-tooltip.top="t('PROJECTS.ISSUES.DELETE')"
                    variant="ghost"
                    color="ruby"
                    size="xs"
                    icon="i-lucide-trash-2"
                    @click="deleteIssue(issue)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>
